import { FC, useCallback } from 'react';
import { DigitalTwin } from 'models';
import Accordion from 'components/UI/Accordion';
import AccordionItem from 'components/UI/Accordion/AccordionItem';
import { useIntl } from 'react-intl';
import { LeafHeader } from '../DigitalTwinCard/styled';

type Props = {
  digitalTwin: DigitalTwin | null;
};

const DigitalTwinTree: FC<Props> = ({ digitalTwin }) => {
  const intl = useIntl();
  const getAccordionItems = useCallback(
    (twin: DigitalTwin | null, title?: string) => {
      if (!twin) {
        return null;
      }

      if (!twin.components || twin.components?.length <= 0) {
        return (
          <AccordionItem
            key={twin.iprId}
            title={title ?? twin.hid ?? twin.iprId}
          >
            <LeafHeader>
              {twin.articleDesc}
            </LeafHeader>
            <AccordionItem
              key="IPRID"
              title={intl.formatMessage({
                id: 'connectivity.iprId',
              })}
              value={twin.iprId}
            />
            <AccordionItem
              key="HID"
              title={intl.formatMessage({
                id: 'connectivity.hid',
              })}
              value={twin.hid}
            />
            <AccordionItem
              key="HID"
              title={intl.formatMessage({
                id: 'connectivity.shortId',
              })}
              value={twin.shortId}
            />
            <AccordionItem
              key="serialNumber"
              title={intl.formatMessage({
                id: 'connectivity.serialNo',
              })}
              value={twin.serialNumber}
            />
            {
              // HIOT-1223 We can't trust that status is NEW if missing MANUFACTURED or DESTROYED in product-api.
              // We do not show any status if we are not sure about the status of the device.
              twin.status === 'NEW' && (
                <AccordionItem
                  key="status"
                  title={intl.formatMessage({
                    id: 'connectivity.status',
                  })}
                  value={twin.status}
                />
              )
            }
            {twin.attachedTo && (
              <AccordionItem
                key="attachedTo"
                title={intl.formatMessage({
                  id: 'connectivity.attached_to',
                })}
                value={twin.attachedTo}
              />
            )}
            {twin.isBleModule && (
              <AccordionItem
                key="PSKs exist"
                title={intl.formatMessage({
                  id: 'connectivity.psks',
                })}
                value={JSON.stringify(twin.hasPsks)}
              />
            )}
            <AccordionItem
              key="pnc"
              title={intl.formatMessage({
                id: 'connectivity.articleNo',
              })}
              value={twin.pnc}
            />
            <AccordionItem
              key="art495"
              title={intl.formatMessage({
                id: 'connectivity.art495',
              })}
              value={twin.art495}
            />
            <AccordionItem
              key="art7"
              title={intl.formatMessage({
                id: 'connectivity.art7',
              })}
              value={twin.art7}
            />
          </AccordionItem>
        );
      }

      return (
        <AccordionItem key={twin.iprId} title={title ?? twin.hid ?? twin.iprId}>
          <LeafHeader>
            {twin.articleDesc}
          </LeafHeader>
          <AccordionItem
            key="IPRID"
            title={intl.formatMessage({
              id: 'connectivity.iprId',
            })}
            value={twin.iprId}
          />
          <AccordionItem
            key="HID"
            title={intl.formatMessage({
              id: 'connectivity.hid',
            })}
            value={twin.hid}
          />
          <AccordionItem
            key="HID"
            title={intl.formatMessage({
              id: 'connectivity.hid',
            })}
            value={twin.shortId}
          />
          <AccordionItem
            key="serialNumber"
            title={intl.formatMessage({
              id: 'connectivity.serialNo',
            })}
            value={twin.serialNumber}
          />
          <AccordionItem
            key="status"
            title={intl.formatMessage({
              id: 'connectivity.status',
            })}
            value={twin.status}
          />
          {twin.isBleModule && (
            <AccordionItem
              key="PSKs exist"
              title={intl.formatMessage({
                id: 'connectivity.psks',
              })}
              value={JSON.stringify(twin.hasPsks)}
            />
          )}
          <AccordionItem
            key="pnc"
            title={intl.formatMessage({
              id: 'connectivity.articleNo',
            })}
            value={twin.pnc}
          />
          <AccordionItem
            key="art495"
            title={intl.formatMessage({
              id: 'connectivity.art495',
            })}
            value={twin.art495}
          />
          <AccordionItem
            key="art7"
            title={intl.formatMessage({
              id: 'connectivity.art7',
            })}
            value={twin.art7}
          />
          {twin.components?.map((comp) => {
            return getAccordionItems(comp.digitalTwin, comp.description);
          })}
        </AccordionItem>
      );
    },
    [intl],
  );

  return (
    <div className="digitalTwin">
      <Accordion>{getAccordionItems(digitalTwin)}</Accordion>
    </div>
  );
};

export default DigitalTwinTree;
